/**
 * Account management hooks — GDPR self-service (#531, #532).
 * Feature doc: docs/canonical/features/future/v1.6-gdpr-self-service.md
 */

import { useOroMutation, OroOperation } from './useOroMutation';
import { accountApi, type DeleteAccountRequestResponse, type DeleteAccountConfirmResponse } from '../lib/api/account';

export function useExportMyData() {
  return useOroMutation<void, void>({
    mutationFn: () => accountApi.exportMyData(),
    successToast: 'Data export downloaded',
    oro: {
      op: OroOperation.ExportMyData,
      retryPolicy: 'never',
      debugLabel: 'GDPR data export',
    },
  });
}

export function useRequestAccountDeletion() {
  return useOroMutation<DeleteAccountRequestResponse, void>({
    mutationFn: () => accountApi.requestDeletion(),
    // Channel-aware: phone-only accounts (#2935) get a texted code, not an
    // emailed link — the toast must not tell them to check an inbox.
    successToast: (data) =>
      data.channel === 'sms'
        ? 'Confirmation code texted — check your phone'
        : 'Confirmation email sent — check your inbox',
    oro: {
      op: OroOperation.RequestAccountDeletion,
      retryPolicy: 'never',
      debugLabel: 'Account deletion request',
    },
  });
}

export function useConfirmAccountDeletion() {
  return useOroMutation<DeleteAccountConfirmResponse, string>({
    mutationFn: (token: string) => accountApi.confirmDeletion(token),
    // No successToast — caller redirects to a sign-out screen so the toast
    // would never be seen.
    oro: {
      op: OroOperation.ConfirmAccountDeletion,
      retryPolicy: 'never',
      debugLabel: 'Account deletion confirm',
    },
  });
}

/**
 * Confirm deletion with the 6-digit SMS code (#2935 / #2980 web parity).
 * Phone-only accounts can sign into the web app (LoginModal auto-detects a
 * phone number), so a phone-only user who deletes on web is texted a code and
 * needs somewhere to enter it — without this the web flow dead-ends exactly as
 * the emailed link would for a mailbox-less account. Mirrors the native
 * `useConfirmAccountDeletionByCode` (mobile/src/hooks/useAccount.ts).
 */
export function useConfirmAccountDeletionByCode() {
  return useOroMutation<DeleteAccountConfirmResponse, string>({
    mutationFn: (code: string) => accountApi.confirmDeletionByCode(code),
    // No successToast — caller wipes client cache and redirects home.
    oro: {
      op: OroOperation.ConfirmAccountDeletion,
      retryPolicy: 'never',
      debugLabel: 'Account deletion confirm (SMS code)',
    },
  });
}
